import React, { useState, useEffect } from "react";
import styled from "styled-components";

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 3rem;
  position: sticky;
  top: 0;
  z-index: 1000;
  backdrop-filter: blur(20px) saturate(180%);
  background-color: ${props => props.scrolled ? 'rgba(11, 14, 20, 0.95)' : 'rgba(11, 14, 20, 0.5)'};
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.scrolled ? '0 4px 24px rgba(0, 0, 0, 0.3)' : 'none'};

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    background-color: rgba(11, 14, 20, 0.98);
    flex-direction: column;
    gap: 1rem;
    transform: ${props => props.hidden ? 'translateY(-100%)' : 'translateY(0)'};
  }
`;

const Logo = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: #fff;
  font-family: "Outfit", "Inter", sans-serif;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, #4A90E2 0%, #9013FE 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.05);
  }
`;

const NavLinks = styled.nav`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const NavLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-family: "Outfit", "Inter", sans-serif;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(74, 144, 226, 0.2), rgba(144, 19, 254, 0.2));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    color: #fff;
    border-color: rgba(74, 144, 226, 0.5);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(74, 144, 226, 0.2);
    
    &::before {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    width: 38px;
    height: 38px;
    font-size: 0.95rem;
    border-radius: 10px;
  }
`;

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScrollY = React.useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isScrolled = currentScrollY > 20;
      setScrolled(isScrolled);

      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setHidden(true);
      } else {
        setHidden(false);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <HeaderContainer scrolled={scrolled} hidden={hidden}>
      <a
        href="/#"
        onClick={(e) => {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      >
        <Logo>SW</Logo>
      </a>
      <NavLinks>
        <NavLink
          href="#mission"
          title="Summary"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("#mission");
          }}
        >
          Su
        </NavLink>
        <NavLink
          href="#experience"
          title="Experience"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("#experience");
          }}
        >
          Xp
        </NavLink>
        <NavLink
          href="#education"
          title="Education"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("#education");
          }}
        >
          Ed
        </NavLink>
        <NavLink
          href="#projects"
          title="Projects"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("#projects");
          }}
        >
          Pr
        </NavLink>
      </NavLinks>
    </HeaderContainer>
  );
};

export default Header;